import SEO from "@/components/site/SEO";
import PageHero from "@/components/site/PageHero";
import BrandCard from "@/components/site/BrandCard";
import CTASection from "@/components/site/CTASection";
import FAQSection from "@/components/site/FAQSection";
import { BRANDS, COMPANY, FAQS_BRANDS } from "@/data/content";

const TRAC_PRODUCTS = [
  {
    name: "TRAC-CMS",
    label: "Competence Management System",
    description:
      "A structured competence management system for recording assessments, monitoring safety-critical competence, managing evidence, and supporting assurance-led decision making.",
    to: "/trac-cms",
    logo: "/TRAC-CMS.svg",
  },
  {
    name: "TRAC-Learn",
    label: "Learning Management System",
    description:
      "A rail-focused learning management system for delivering training, tracking completion, assigning learning pathways, and maintaining auditable training records.",
    to: "/trac-learn",
    logo: "/TRAC-Learn.svg",
  },
];

export default function OurBrands() {
  return (
    <>
      <SEO
        title="Our Brands | UKTDA"
        description="Train Driver Foundation, Train Driver Psychometrics, Railway Careers UK and TRAC — the specialist brands and platforms of UKTDA."
        path="/our-brands"
      />

      <PageHero
        eyebrow="Our brands"
        title="A connected rail ecosystem supporting candidates, careers, training, competence and assurance."
        intro={`${COMPANY.legalName} brings together specialist rail-sector brands and platforms — each designed to support a distinct part of the railway career, training or organisational capability journey.`}
      />

      <section className="py-20 md:py-28 bg-white" data-testid="brands-grid">
        <div className="container-rail">
          <div className="max-w-8xl mb-16">
            <p className="label-eyebrow text-safety">Group structure</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Candidate preparation, psychometric practice, career guidance,
              professional training and enterprise assurance under one company.
            </h2>

            <p className="mt-4 text-slate-600 leading-relaxed">
              Each brand has a clear role. TRAC provides the enterprise platform
              layer for training, competence, risk and assurance compliance.
              Train Driver Foundation supports candidate readiness, Train Driver
              Psychometrics focuses on structured test familiarisation, and
              Railway Careers UK supports career research and discovery.
            </p>
          </div>

          {/* TRAC SECTION */}
          <div
            className="bg-navy-deep text-white rounded-md overflow-hidden"
            data-testid="trac-brand-feature"
          >
            <div className="grid lg:grid-cols-[1.1fr_1.4fr]">
              <div className="p-8 md:p-12 border-b lg:border-b-0 lg:border-r border-white/10">
                <p className="label-eyebrow text-safety">
                  Enterprise platform
                </p>

                <h3 className="mt-3 font-display font-bold text-2xl md:text-4xl leading-tight text-white">
                  TRAC — Training, Risk and Assurance Compliance.
                </h3>

                <p className="mt-5 text-slate-300 leading-relaxed">
                  TRAC is the professional systems layer within the UKTDA
                  ecosystem. It is designed for organisations that need stronger
                  control over training delivery, competence evidence, assurance
                  activity, operational risk and compliance visibility.
                </p>

                <p className="mt-5 text-slate-300 leading-relaxed">
                  Through TRAC-CMS and TRAC-Learn, organisations can strengthen
                  how they manage competence records, learning delivery,
                  training evidence, compliance visibility and assurance-led
                  decision making.
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href="/trac"
                    className="inline-flex items-center justify-center rounded-sm bg-safety px-5 py-3 text-sm font-bold text-navy hover:bg-white transition"
                    data-testid="brands-trac-link"
                  >
                    Explore TRAC
                  </a>

                  <a
                    href="/contact"
                    className="inline-flex items-center justify-center rounded-sm border border-white/20 px-5 py-3 text-sm font-bold text-white hover:bg-white hover:text-navy transition"
                    data-testid="brands-trac-contact"
                  >
                    Discuss TRAC
                  </a>
                </div>
              </div>

              <div className="p-8 md:p-12">
                <p className="label-eyebrow text-safety">TRAC products</p>

                <div className="mt-6 grid gap-5">
                  {TRAC_PRODUCTS.map((product) => (
                    <a
                      key={product.name}
                      href={product.to}
                      className="group block rounded-md border border-white/10 bg-white/5 p-6 md:p-7 hover:bg-white/10 transition"
                      data-testid={`brands-${product.name
                        .toLowerCase()
                        .replace("-", "-")}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5">
                        <div className="flex flex-col sm:flex-row gap-5">
                          <div className="h-28 w-28 md:h-32 md:w-32 flex-shrink-0 rounded-md bg-white p-4 flex items-center justify-center shadow-sm">
                            <img
                              src={product.logo}
                              alt={`${product.name} logo`}
                              className="max-h-full max-w-full object-contain"
                            />
                          </div>

                          <div>
                            <h4 className="font-display font-bold text-xl md:text-2xl text-white">
                              {product.name}
                            </h4>

                            <p className="mt-1 text-sm font-semibold text-safety">
                              {product.label}
                            </p>

                            <p className="mt-4 text-slate-300 leading-relaxed">
                              {product.description}
                            </p>
                          </div>
                        </div>

                        <span className="text-sm font-bold text-slate-300 group-hover:text-white transition whitespace-nowrap">
                          Learn more →
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* CANDIDATE / CAREER BRANDS SECTION */}
          <div className="mt-16">
            <div className="max-w-4xl mb-10">
              <p className="label-eyebrow text-safety">
                Candidate and career brands
              </p>

              <h3 className="mt-3 font-display font-bold text-2xl md:text-3xl text-navy leading-tight">
                Specialist support for aspiring train drivers and railway career
                changers.
              </h3>

              <p className="mt-4 text-slate-600 leading-relaxed">
                Alongside TRAC, UKTDA operates a connected set of candidate-facing
                brands that support people through research, preparation,
                psychometric familiarisation and the wider train driver
                recruitment journey.
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {BRANDS.map((brand) => (
                <BrandCard key={brand.slug} brand={brand} />
              ))}
            </div>
          </div>

          {/* B2B TRAINING SECTION */}
          <div className="mt-16 bg-slate-50 border border-slate-200 rounded-md p-8 md:p-12 grid lg:grid-cols-[2fr_3fr] gap-8 items-center">
            <div>
              <p className="label-eyebrow text-safety">B2B training</p>

              <h3 className="mt-3 font-display font-bold text-2xl md:text-3xl text-navy leading-tight">
                Professional railway training, delivered directly by UKTDA.
              </h3>
            </div>

            <p className="text-slate-600 leading-relaxed">
              Bespoke training, instructor development, driver manager
              development, human factors, fatigue and NTS workshops, competence
              support, and operational scenario design — tailored to operators,
              training providers, heritage railways and operational teams. TRAC
              can support this work by providing a structured platform for
              training records, competence evidence and assurance activity.
            </p>
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Discuss requirements"
        title="Which brand or platform is right for you?"
        intro="If you are not sure whether you need candidate preparation, psychometric familiarisation, career guidance, direct training or a TRAC platform solution, we can point you in the right direction."
        primary={{
          label: "Contact us",
          to: "/contact",
          testId: "brands-cta-contact",
        }}
        secondary={{
          label: "Explore TRAC",
          to: "/trac",
          testId: "brands-cta-trac",
        }}
      />

      <FAQSection
        items={FAQS_BRANDS}
        title="About our brands"
        intro="How Train Driver Foundation, Train Driver Psychometrics, Railway Careers UK and TRAC fit together under UKTDA."
        schemaId="faq-brands"
      />
    </>
  );
}